import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { getDepartments, getGetDepartmentsQueryKey } from '@vlabs/api/artemy/client';
import type { DepartmentId, DepartmentRecord } from '@vlabs/api/artemy/model';

import type { TreeDataNode } from '@vlabs/monokit/shared/ui/tree';

interface DepartmentTreeDataNode extends TreeDataNode {
  key: DepartmentId;
  name: string;
  department: DepartmentRecord;
  level: number;
  children?: DepartmentTreeDataNode[];
}

const DEPARTMENTS_PAGE_SIZE = 1000;
const MAX_DEPARTMENT_LEVEL = 5;
const DEPARTMENT_TREE_QUERY_KEY = getGetDepartmentsQueryKey();
const TREE_NODE_WITHOUT_SWITCHER_CLASS = 'DepartmentsTreeNode_withoutSwitcher';

async function getAllDepartments(): Promise<DepartmentRecord[]> {
  const departments: DepartmentRecord[] = [];
  let page = 1;
  let hasNextPage = true;

  while (hasNextPage) {
    // в ответе нет total/next, поэтому конец списка определяем по размеру страницы
    // eslint-disable-next-line no-await-in-loop
    const response = await getDepartments({ page, page_size: DEPARTMENTS_PAGE_SIZE });
    const pageDepartments = response.departments ?? [];

    departments.push(...pageDepartments);
    hasNextPage = pageDepartments.length === DEPARTMENTS_PAGE_SIZE;
    page += 1;
  }

  return departments;
}

// собираем плоский список подразделений в дерево по parent_department_id
function buildDepartmentTree(
  departments: DepartmentRecord[],
  parentDepartmentId: DepartmentId | undefined = undefined,
  level = 1,
): DepartmentTreeDataNode[] {
  return departments
    .filter((department) => (
      parentDepartmentId
        ? department.parent_department_id === parentDepartmentId
        : !department.parent_department_id
    ))
    .map((department) => {
      const departmentId = department.department_id;
      const children = level < MAX_DEPARTMENT_LEVEL
        ? buildDepartmentTree(departments, departmentId, level + 1)
        : [];

      return {
        // title оставляем пустым, чтобы rc-tree не показывал нативный tooltip
        // название подразделения рендерим сами через name
        key: departmentId,
        title: '',
        name: department.name ?? departmentId,
        department,
        // у первого и пятого уровня по макету нет switcher-иконки и отступа под нее
        className: level === 1 || level === MAX_DEPARTMENT_LEVEL ? TREE_NODE_WITHOUT_SWITCHER_CLASS : undefined,
        level,
        ...(children.length ? { children } : {}),
      };
    });
}

function useDepartmentTree(): UseQueryResult<DepartmentTreeDataNode[]> {
  return useQuery({
    queryKey: DEPARTMENT_TREE_QUERY_KEY,
    queryFn: async () => buildDepartmentTree(await getAllDepartments()),
  });
}

export { DEPARTMENT_TREE_QUERY_KEY, MAX_DEPARTMENT_LEVEL, useDepartmentTree };
export type { DepartmentTreeDataNode };
